import {
    HttpContextToken,
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpInterceptorFn,
    HttpRequest
} from "@angular/common/http";
import { inject, PLATFORM_ID } from "@angular/core";
import { isPlatformBrowser } from "@angular/common";
import { Observable, from, throwError } from "rxjs";
import { catchError, finalize, switchMap } from "rxjs/operators";
import { Router } from "@angular/router";
import { AuthService } from "./auth.service";
import { LoadingService } from "../services/loading.service";

// Marca requisições já reenviadas após um refresh, evitando loop de retry
const AUTH_RETRIED = new HttpContextToken<boolean>(() => false);

export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> => {
    const router = inject(Router);
    const authService = inject(AuthService);
    const loadingService = inject(LoadingService);

    const platformId = inject(PLATFORM_ID);
    let newReq = req.clone();
    let countLoaderBlock = 0;
    let loaderTracked = false;
    let newHeaders = req.headers;

    if (isPlatformBrowser(platformId)) {
        const token = authService.token();
        if (token) {
            newReq = req.clone({
                setHeaders: {
                    Authorization: `Bearer ${token}`
                }
            });
        }

        if (req.url.includes('viacep.com.br/ws')) {
            countLoaderBlock++;
        }

        // Uploads têm indicador próprio por slot e bloqueiam a navegação;
        // o loader global de tela cheia cobriria a UI durante todo o envio.
        if (req.url.includes('/upload/')) {
            countLoaderBlock++;
        }

        if (req.body?.operationName) {
            // GetVendasDashboard/GetNiveisDashboard/GetParcelasFinanceiro rodam em
            // segundo plano no shell (checagem de promoção a cada navegação) e as
            // telas que as usam têm loading próprio — não devem acionar a barra global.
            ['.svg', '/assets', '/svg', 'calculateAlignerPlan', 'refreshToken',
                'GetVendasDashboard', 'GetNiveisDashboard', 'GetParcelasFinanceiro'].forEach(e => {
                if (req.body.operationName.includes(e)) {
                    countLoaderBlock++;
                }
            });
        }

        if (countLoaderBlock === 0) {
            loaderTracked = true;
            loadingService.taskStarted();
        }
    }

    return next(newReq).pipe(
        catchError((error: HttpErrorResponse) => {
            if (error.status === 401 && !req.context.get(AUTH_RETRIED)) {
                // Tenta renovar a sessão e reenviar a requisição uma única vez
                return from(authService.refreshSession()).pipe(
                    switchMap(token => {
                        if (!token) {
                            authService.logout();
                            return throwError(() => error);
                        }
                        const retriedReq = newReq.clone({
                            setHeaders: { Authorization: `Bearer ${token}` },
                            context: newReq.context.set(AUTH_RETRIED, true)
                        });
                        return next(retriedReq);
                    })
                );
            }
            if (error.status === 401) {
                authService.logout();
            }
            return throwError(() => error);
        }),
        finalize(() => {
            if (loaderTracked) {
                loadingService.taskFinished();
            }
        })
    );

};